import { useEffect, useRef, useState } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../../lib/api";

const PINK = [236, 72, 153];
const INDIGO = [108, 108, 240];
const GREY = "#5b6270";
const lerp = (a, b, t) => Math.round(a + (b - a) * t);
const seg = (t) => `rgb(${lerp(PINK[0], INDIGO[0], t)},${lerp(PINK[1], INDIGO[1], t)},${lerp(PINK[2], INDIGO[2], t)})`;

// Muted dark slate basemap (Lugg look): deep navy-slate ground, dim parks, dark water, grey roads.
const DARK_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#1b2231" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#141a26" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#7c8598" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#283143" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#1a3128" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#557a68" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#2a3242" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#8791a3" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#313a4c" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3d465c" }] },
  { featureType: "road.highway.controlled_access", elementType: "geometry", stylers: [{ color: "#48526b" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e1a2c" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#33506f" }] },
];

// ---- Google loader singleton (survives re-renders / Strict Mode) ----
let googleLoad;
let authFailed = false;
let handlerSet = false;
function loadGoogle(key) {
  if (!key) return Promise.reject(new Error("no key"));
  if (!googleLoad) {
    if (!handlerSet) {
      const prev = window.gm_authFailure;
      window.gm_authFailure = () => { authFailed = true; prev && prev(); };
      handlerSet = true;
    }
    setOptions({ key, v: "weekly", authReferrerPolicy: "origin" });
    googleLoad = Promise.race([
      importLibrary("maps"),
      new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 9000)),
    ]).then(async (maps) => {
      if (authFailed) throw new Error("auth failed");
      const marker = await importLibrary("marker");
      return { ...maps, Marker: marker.Marker };
    }).catch((e) => { googleLoad = undefined; throw e; });
  }
  return googleLoad;
}

const markerSvg = (n) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
    <path d="M20 2C10.6 2 3 9.5 3 18.7 3 31 20 48 20 48s17-17 17-29.3C37 9.5 29.4 2 20 2z" fill="#6C6CF0" stroke="#fff" stroke-width="2.2"/>
    <circle cx="20" cy="18.7" r="10.5" fill="#0b0b0d"/>
    <text x="20" y="24" text-anchor="middle" font-family="Arial" font-size="15" font-weight="800" fill="#fff">${n}</text>
  </svg>`;
  return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
};

// Pulsing "you are here" dot (animated SMIL, works as an <img>/marker icon).
const ME_SVG = "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
    <circle cx="32" cy="32" r="9" fill="#4A6CF7" opacity="0.35">
      <animate attributeName="r" values="9;28;9" dur="2.1s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0.5;0;0.5" dur="2.1s" repeatCount="indefinite"/>
    </circle>
    <circle cx="32" cy="32" r="8" fill="#4A6CF7" stroke="#fff" stroke-width="3"/>
  </svg>`);

const allPoints = (me, pins, activePath, greyPath) => {
  const out = [];
  if (me) out.push(me);
  (pins || []).forEach((p) => out.push(p));
  (activePath || []).forEach((p) => out.push(p));
  (greyPath || []).forEach((p) => out.push(p));
  return out.filter((p) => p && p.lat != null && p.lng != null);
};

function GoogleMap({ me, pins, activePath, greyPath }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const gRef = useRef(null);
  const meRef = useRef(null);

  const placeMe = () => {
    const g = gRef.current, map = mapRef.current;
    if (!g || !map || !me) return;
    const pos = { lat: me.lat, lng: me.lng };
    if (!meRef.current) {
      meRef.current = new g.Marker({
        map, position: pos, clickable: false, zIndex: 9999,
        icon: { url: ME_SVG, scaledSize: new window.google.maps.Size(64, 64), anchor: new window.google.maps.Point(32, 32) },
      });
    } else meRef.current.setPosition(pos);
  };

  useEffect(() => {
    let alive = true; const objs = [];
    api.get("/geo/maps-key").then((r) => loadGoogle(r.data.key)).then((g) => {
      if (!alive || !el.current) return;
      gRef.current = g;
      const pts = allPoints(me, pins, activePath, greyPath);
      const center = pts[0] || { lat: 32.7157, lng: -117.1611 };
      const map = new g.Map(el.current, {
        center, zoom: 12, disableDefaultUI: true, gestureHandling: "greedy",
        backgroundColor: "#1b2231", clickableIcons: false, styles: DARK_STYLE,
      });
      mapRef.current = map;
      // grey upcoming leg first (under the active one)
      if (greyPath && greyPath.length > 1) {
        objs.push(new window.google.maps.Polyline({
          map, path: greyPath, strokeColor: GREY, strokeOpacity: 0.9, strokeWeight: 6,
        }));
      }
      // active gradient leg (crew -> current stop)
      if (activePath && activePath.length > 1) {
        const n = activePath.length - 1;
        for (let i = 0; i < n; i++) {
          objs.push(new window.google.maps.Polyline({
            map, path: [activePath[i], activePath[i + 1]],
            strokeColor: seg(n > 1 ? i / (n - 1) : 0), strokeOpacity: 1, strokeWeight: 6,
          }));
        }
      }
      (pins || []).forEach((p) => objs.push(new g.Marker({
        map, position: { lat: p.lat, lng: p.lng },
        icon: { url: markerSvg(p.n), scaledSize: new window.google.maps.Size(40, 50), anchor: new window.google.maps.Point(20, 48) },
      })));
      const b = new window.google.maps.LatLngBounds();
      pts.forEach((p) => b.extend(p));
      if (!b.isEmpty()) map.fitBounds(b, { top: 80, right: 55, bottom: Math.round(window.innerHeight * 0.5), left: 55 });
      placeMe();
    }).catch(() => {});
    return () => {
      alive = false;
      objs.forEach((o) => o.setMap && o.setMap(null));
      if (meRef.current) { meRef.current.setMap(null); meRef.current = null; }
      mapRef.current = null; gRef.current = null;
    };
    // eslint-disable-next-line
  }, [pins, activePath, greyPath]);

  useEffect(() => { placeMe(); }, [me?.lat, me?.lng]); // eslint-disable-line
  return <div ref={el} style={{ height: "100%", width: "100%", background: "#1b2231" }} />;
}

const leafPin = (n) => L.divIcon({
  className: "", iconSize: [40, 50], iconAnchor: [20, 48],
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
    <path d="M20 2C10.6 2 3 9.5 3 18.7 3 31 20 48 20 48s17-17 17-29.3C37 9.5 29.4 2 20 2z" fill="#6C6CF0" stroke="#fff" stroke-width="2.2"/>
    <circle cx="20" cy="18.7" r="10.5" fill="#0b0b0d"/>
    <text x="20" y="24" text-anchor="middle" font-family="Arial" font-size="15" font-weight="800" fill="#fff">${n}</text>
  </svg>`,
});
const meLeafIcon = () => L.divIcon({
  className: "", iconSize: [64, 64], iconAnchor: [32, 32],
  html: `<img src="${ME_SVG}" width="64" height="64" style="display:block" alt="" />`,
});

function MeMarker({ me }) {
  const map = useMap();
  const ref = useRef(null);
  useEffect(() => {
    if (!me) return;
    if (!ref.current) {
      ref.current = L.marker([me.lat, me.lng], { icon: meLeafIcon(), interactive: false, zIndexOffset: 1000, keyboard: false }).addTo(map);
    } else ref.current.setLatLng([me.lat, me.lng]);
  }, [me?.lat, me?.lng, map]);
  useEffect(() => () => { if (ref.current) { map.removeLayer(ref.current); ref.current = null; } }, [map]);
  return null;
}

function FitBounds({ pts }) {
  const map = useMap();
  useEffect(() => {
    if (pts.length >= 2) map.fitBounds(L.latLngBounds(pts.map((p) => [p.lat, p.lng])), { paddingTopLeft: [55, 80], paddingBottomRight: [55, Math.round(window.innerHeight * 0.5)] });
    else if (pts.length === 1) map.setView([pts[0].lat, pts[0].lng], 13);
    // eslint-disable-next-line
  }, [pts.length, pts[0]?.lat, pts[0]?.lng]);
  return null;
}
function LeafletMap({ me, pins, activePath, greyPath }) {
  const pts = allPoints(me, pins, activePath, greyPath);
  const center = pts[0] || { lat: 32.7157, lng: -117.1611 };
  const gradSegs = [];
  if (activePath && activePath.length > 1) {
    const n = activePath.length - 1;
    for (let i = 0; i < n; i++)
      gradSegs.push(<Polyline key={`a${i}`} positions={[[activePath[i].lat, activePath[i].lng], [activePath[i + 1].lat, activePath[i + 1].lng]]}
        pathOptions={{ color: seg(n > 1 ? i / (n - 1) : 0), weight: 6, opacity: 0.98, lineCap: "round" }} />);
  }
  return (
    <MapContainer center={[center.lat, center.lng]} zoom={11} className="map-dark-tiles" style={{ height: "100%", width: "100%", background: "#1b2231" }}
      zoomControl={false} attributionControl={false} scrollWheelZoom={false} dragging={true} touchZoom={true} doubleClickZoom={true}>
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      {greyPath && greyPath.length > 1 && (
        <Polyline positions={greyPath.map((p) => [p.lat, p.lng])} pathOptions={{ color: GREY, weight: 6, opacity: 0.9, lineCap: "round" }} />
      )}
      {gradSegs}
      {(pins || []).map((p, i) => <Marker key={i} position={[p.lat, p.lng]} icon={leafPin(p.n)} />)}
      {me && <MeMarker me={me} />}
      <FitBounds pts={pts} />
    </MapContainer>
  );
}

export default function MoveMap({ me, pins = [], activePath = null, greyPath = null }) {
  const [mode, setMode] = useState("loading");
  useEffect(() => {
    const prev = window.gm_authFailure;
    window.gm_authFailure = () => { authFailed = true; setMode("leaflet"); prev && prev(); };
    return () => { window.gm_authFailure = prev; };
  }, []);
  useEffect(() => {
    let alive = true;
    api.get("/geo/maps-key").then((r) => loadGoogle(r.data.key))
      .then(() => alive && setMode((m) => (m === "leaflet" ? m : "google")))
      .catch(() => alive && setMode("leaflet"));
    return () => { alive = false; };
  }, []);
  if (mode === "loading") return <div style={{ height: "100%", width: "100%", background: "#1b2231" }} />;
  return mode === "google"
    ? <GoogleMap me={me} pins={pins} activePath={activePath} greyPath={greyPath} />
    : <LeafletMap me={me} pins={pins} activePath={activePath} greyPath={greyPath} />;
}
